import { z } from 'zod';
import { COST_CATEGORIES, DELIVERY_CHANNELS } from '../enums';
import { zCents, zDate, zId, zReason } from './common';

export const CreatePeriodBody = z.object({ propertyId: zId, from: zDate, to: zDate }).refine((v) => v.from <= v.to, { message: 'Beginn nach Ende', path: ['to'] });
export const UpdatePeriodBody = z.object({
  /** `unitCostCents`: CO2 cost of the flat as stated in the WEG Hausgeldabrechnung, replaces the computed share (docs/15 #52). */
  co2: z.object({ kgTotal: z.number().min(0), priceCentsPerTon: z.number().min(0), heatingAreaTotal: z.number().positive().optional(), unitCostCents: zCents.min(0).optional() }).nullable().optional(),
  prepaymentBasis: z.enum(['target', 'actual']).nullable().optional(),
  /** CO2KostAufG does not apply to this period (no fossil fuel …): reason required, null clears it. */
  co2NotApplicable: z.object({ reason: zReason }).nullable().optional(),
  /** § 556 Abs. 3 S. 3 BGB: landlord is not responsible for the late statement (reason required); also on final periods. */
  lateNotResponsible: z.object({ reason: zReason }).nullable().optional(),
});
/** `resend`: deliver again to recipients already served on this channel (otherwise they are skipped, e.g. on a retry). */
export const SendStatementBody = z.object({ recipients: z.array(zId).min(1), channel: z.enum(DELIVERY_CHANNELS), resend: z.boolean().default(false) });
export const HeatingImportBody = z.object({
  billingPeriodId: zId,
  source: z.string().max(40).default('import'),
  rows: z.array(z.object({ costCategory: z.enum(COST_CATEGORIES), tenancyId: zId.optional(), unitId: zId.optional(), unitLabel: z.string().optional(), amountCents: zCents })).optional(),
  csv: z.string().max(2_000_000).optional(),
  /** Totals per cost category from the provider's statement; when given, the rows of that category must add up exactly. */
  totals: z.partialRecord(z.enum(COST_CATEGORIES), zCents.min(0)).optional(),
}).refine((v) => !!v.rows || !!v.csv, { message: 'Zeilen oder CSV angeben' });

/**
 * Heizkostenabrechnung of the metering service (ista, Techem, Brunata, Minol, Kalo …) as PDF: step 1 reads the uploaded
 * documents with the AI (POST /billing-periods/:id/heating/extract), step 2 saves what the person checked and mapped
 * (POST /billing-periods/:id/heating/confirm). Nothing is written to the billing before step 2.
 */
export const HEATING_CATEGORIES = ['heating', 'hot_water'] as const;
export const HeatingExtractBody = z.object({
  documentIds: z.array(zId).min(1).max(5),
  /** read again with the AI even when the same files were read before */
  force: z.boolean().default(false),
});
export const HeatingConfirmBody = z.object({
  documentIds: z.array(zId).min(1).max(5),
  vendor: z.string().trim().max(200).optional(),
  serviceFrom: zDate,
  serviceTo: zDate,
  totals: z.partialRecord(z.enum(HEATING_CATEGORIES), zCents.min(0)),
  /** create the cost items heating / hot water from the totals (off when the period already has them) */
  createCostItems: z.boolean().default(true),
  rows: z.array(z.object({ costCategory: z.enum(HEATING_CATEGORIES), tenancyId: zId.optional(), unitId: zId.optional(), amountCents: zCents.min(0) })
    .refine((r) => !!r.tenancyId !== !!r.unitId, { message: 'Einheit oder Mietverhältnis angeben' })).min(1).max(400),
}).refine((v) => v.serviceFrom <= v.serviceTo, { message: 'Leistungszeitraum: Beginn nach Ende', path: ['serviceTo'] });

/** Who a user (Nutzer) of the provider's statement is in ImmoAktas, and how the match was found. */
export type HeatingMatch = 'meter' | 'name' | 'label' | 'order' | 'manual';
export type HeatingTarget = { unitId?: string; tenancyId?: string };
export type HeatingExtractionRow = {
  index: number;
  name: string | null;
  userNo: string | null;
  position: string | null;
  meterNos: string[];
  from: string | null;
  to: string | null;
  heatingCents: number | null;
  hotWaterCents: number | null;
  target: HeatingTarget | null;
  match: HeatingMatch | null;
  /** false: the person has to check the mapping (shown like a low-confidence field) */
  sure: boolean;
};
export type HeatingExtractionIssue =
  | { code: 'sum_mismatch'; category: (typeof HEATING_CATEGORIES)[number]; sumCents: number; totalCents: number }
  | { code: 'period_mismatch'; from: string | null; to: string | null }
  | { code: 'no_rows' }
  | { code: 'unmapped'; count: number }
  /** Zuordnungsprüfung: address or meters of the statement belong to another property (or to none of the account's). */
  | { code: 'property_mismatch'; propertyId: string | null; propertyName: string | null; address: string | null };
export type HeatingExtraction = {
  documentIds: string[];
  vendor: string | null;
  serviceFrom: string | null;
  serviceTo: string | null;
  totals: { heating: number | null; hot_water: number | null };
  rows: HeatingExtractionRow[];
  issues: HeatingExtractionIssue[];
  /** active heating / hot water cost items that already cover the period (then only the results per user are saved by default) */
  existingCostItems: { id: string; category: string; label: string; amountCents: number; documentId: string | null }[];
  /** everyone the costs can go to: units and tenancies of the period */
  targets: { key: string; unitId: string; tenancyId?: string; label: string }[];
  /** true when the AI was not called again (same files read before) */
  reused: boolean;
};

/**
 * POST /billing-periods/backfill ("Frühere Jahre nachtragen"): one period per past year, in the property's rhythm
 * (the period starting in that year). `extendUnits`: units that exist since the first billing period are taken back
 * to the start of the earliest new period (recorded as `unit.backdated` event).
 */
export const BACKFILL_MAX_YEARS = 15;
export const BackfillPeriodsBody = z.object({
  propertyId: zId,
  years: z.array(z.number().int().min(1990).max(2100)).min(1).max(BACKFILL_MAX_YEARS).refine((y) => new Set(y).size === y.length, 'duplicate years'),
  extendUnits: z.boolean().default(true),
});

/** The period of `year` in a rhythm starting on `start` ("MM-DD"): it starts in that year (server rule, see `periodOfYear`). */
export function periodInYear(start: string, year: number): { from: string; to: string } {
  const [m, d] = start.split('-').map(Number) as [number, number];
  // 29 Feb only exists in leap years: such a rhythm starts on 1 March otherwise
  const leap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const md = (y: number) => (m === 2 && d === 29 && !leap(y) ? '03-01' : start);
  const from = `${year}-${md(year)}`;
  const next = new Date(Date.UTC(year + 1, Number(md(year + 1).slice(0, 2)) - 1, Number(md(year + 1).slice(3, 5)) - 1));
  return { from, to: next.toISOString().slice(0, 10) };
}
